import { Box, Text, Group, Badge, SimpleGrid, Stack, Button, Radio, Checkbox, Flex, Divider } from "@mantine/core";
import type { KeyboardEvent, CSSProperties } from "react";
import type {
  Question,
  Answer,
  ScaleAnswer,
  PolicyAnswer,
  MultiSelectAnswer,
} from "@/utils/gradingEngine.types";

const ROW_BORDER = "var(--mantine-color-primary-3)";
const ROW_SELECTED = "var(--mantine-color-primary-1)";
const SLATE_LINE = "var(--mantine-color-slate-2)";

type Props = {
  question: Question;
  answer: Answer | undefined;
  readOnly?: boolean;
  onChange: (answer: Answer) => void;
  onClear: () => void;
  showDivider?: boolean;
};

const OPTION_ROW_CONTROL_STYLES = {
  root: { width: "100%", pointerEvents: "none" as const, alignItems: "center" },
  body: { alignItems: "center" },
  label: {
    width: "100%",
    fontSize: 13,
    lineHeight: 1.35,
    paddingTop: 0,
    paddingBottom: 0,
  },
  inner: { pointerEvents: "none" as const },
};

function optionRowStyle(selected: boolean, interactive: boolean): CSSProperties {
  return {
    borderRadius: 8,
    border: `1px solid ${selected ? ROW_BORDER : SLATE_LINE}`,
    background: selected ? ROW_SELECTED : "#ffffff",
    padding: "6px 12px",
    minHeight: 36,
    display: "flex",
    alignItems: "center",
    transition: "background 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
    boxShadow: selected ? "0 0 0 1px rgba(4, 48, 92, 0.06)" : undefined,
    cursor: interactive ? "pointer" : "default",
    outline: "none",
  };
}

function rowKeyActivate(fn: () => void, disabled: boolean) {
  return (e: KeyboardEvent) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      fn();
    }
  };
}

export default function AssessmentQuestionField({
  question,
  answer,
  readOnly = false,
  onChange,
  onClear,
  showDivider = true,
}: Props) {
  const hasAnswer =
    question.type === "multi_select"
      ? ((answer as MultiSelectAnswer | undefined)?.selectedOptionIds?.length ?? 0) > 0
      : !!(answer as ScaleAnswer | PolicyAnswer | undefined)?.selectedOptionId;

  const singleSelected =
    question.type === "multi_select"
      ? null
      : ((answer as ScaleAnswer | PolicyAnswer | undefined)?.selectedOptionId ?? null);

  const typeLabel =
    question.type === "scale"
      ? "SCALE"
      : question.type === "policy"
        ? "SINGLE CHOICE"
        : "MULTIPLE CHOICE";

  const handleSingleChange = (optionId: string) => {
    if (readOnly) return;
    const next: ScaleAnswer | PolicyAnswer = { questionId: question.id, selectedOptionId: optionId };
    onChange(next);
  };

  const handleMultiToggle = (optionId: string) => {
    if (readOnly) return;
    const current = (answer as MultiSelectAnswer | undefined)?.selectedOptionIds ?? [];
    const nextIds = current.includes(optionId) ? current.filter((id) => id !== optionId) : [...current, optionId];
    onChange({ questionId: question.id, selectedOptionIds: nextIds } as MultiSelectAnswer);
  };

  return (
    <Box>
      {showDivider && <Divider my="xs" color="slate.2" />}
      <Stack gap={6}>
        <Flex justify="space-between" align="flex-start" gap="md" wrap="nowrap">
          <Text fw={600} fz="md" c="primary.8" style={{ flex: 1, lineHeight: 1.5 }}>
            {question.text}
            {question.required && (
              <Text component="span" c="yellow.8" ml={6} fw={700}>
                *
              </Text>
            )}
          </Text>
          <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
            {hasAnswer && !readOnly && (
              <Button
                variant="default"
                size="xs"
                onClick={onClear}
                styles={{
                  root: {
                    backgroundColor: "var(--mantine-color-slate-1)",
                    borderColor: "var(--mantine-color-slate-2)",
                    color: "var(--mantine-color-slate-7)",
                  },
                }}
              >
                Clear
              </Button>
            )}
            <Badge
              variant="outline"
              color="slate"
              size="sm"
              radius="sm"
              styles={{
                root: {
                  textTransform: "none",
                  borderColor: "var(--mantine-color-slate-3)",
                  color: "var(--mantine-color-slate-6)",
                },
              }}
            >
              {typeLabel}
            </Badge>
          </Group>
        </Flex>

        <Box>
        {(question.type === "scale" || question.type === "policy") && (
          <Radio.Group
            value={(answer as ScaleAnswer | PolicyAnswer | undefined)?.selectedOptionId ?? ""}
            onChange={handleSingleChange}
            role="radiogroup"
            aria-label={question.text}
          >
            <SimpleGrid cols={2} spacing={8}>
              {question.options.map((opt) => {
                const selected = singleSelected === opt.id;
                const radioTabStopId = singleSelected ?? question.options[0]?.id;
                return (
                  <Box
                    key={opt.id}
                    role="radio"
                    aria-checked={selected}
                    tabIndex={readOnly ? -1 : opt.id === radioTabStopId ? 0 : -1}
                    style={{ ...optionRowStyle(selected, !readOnly), height: "100%" }}
                    onClick={readOnly ? undefined : () => handleSingleChange(opt.id)}
                    onKeyDown={rowKeyActivate(() => handleSingleChange(opt.id), readOnly)}
                  >
                    <Radio
                      value={opt.id}
                      label={opt.label}
                      disabled={readOnly}
                      color="primary"
                      size="xs"
                      tabIndex={-1}
                      styles={{
                        ...OPTION_ROW_CONTROL_STYLES,
                        radio: { pointerEvents: "none", marginTop: 0 },
                      }}
                    />
                  </Box>
                );
              })}
            </SimpleGrid>
          </Radio.Group>
        )}

        {question.type === "multi_select" && (
          <SimpleGrid cols={2} spacing={8} role="group" aria-label={question.text}>
            {question.options.map((opt) => {
              const selected =
                (answer as MultiSelectAnswer | undefined)?.selectedOptionIds?.includes(opt.id) ?? false;
              return (
                <Box
                  key={opt.id}
                  role="checkbox"
                  aria-checked={selected}
                  tabIndex={readOnly ? -1 : 0}
                  style={{ ...optionRowStyle(selected, !readOnly), height: "100%" }}
                  onClick={readOnly ? undefined : () => handleMultiToggle(opt.id)}
                  onKeyDown={rowKeyActivate(() => handleMultiToggle(opt.id), readOnly)}
                >
                  <Checkbox
                    checked={selected}
                    onChange={() => {}}
                    label={opt.label}
                    disabled={readOnly}
                    color="primary"
                    size="xs"
                    tabIndex={-1}
                    styles={{
                      ...OPTION_ROW_CONTROL_STYLES,
                      input: { pointerEvents: "none", marginTop: 0 },
                    }}
                  />
                </Box>
              );
            })}
          </SimpleGrid>
        )}
        </Box>
      </Stack>
    </Box>
  );
}
